import {
  PdfGen,
  PdfUnit,
  PdfSize,
  PdfRect,
  PdfPoint,
  PdfBrush,
  PdfFont,
  ETextAlign,
} from '../../pdf/pdf';
import { ILayoutImageGen } from '../layoutImage';
import { OptsImage } from '../optsImage';
import { IItemCount } from '@educraft/base';
import { GUtil } from '../../pdf/gutil';
import '../../pdf/draw/primitives';
import '../../pdf/draw/string';
import '../../pdf/draw/image';

/**
 * Jednotky (Place Values) - Number digit decomposition cards
 *
 * Layout: 6 portrait pages, 2x3 grid per page.
 * Page N shows numbers with (N+2) digits. Each card: image, number string,
 * and rounded rectangle boxes for each digit place value.
 *
 * Migrated from C# EduFrame.Img10.Generators.Jednotky
 */
export class Jednotky implements ILayoutImageGen, IItemCount {
  countDefault(): number {
    return 2;
  }
  countMin(): number {
    return 10;
  }
  countMax(): number {
    return 5;
  }

  public async generate(pdf: PdfGen, opts: OptsImage): Promise<void> {
    const margin = PdfUnit.FromMillimeters(5);
    const cX = 2,
      cY = 3;
    const size = { width: PdfSize.A4.width / cX, height: PdfSize.A4.height / cY };

    const names = [
      opts.data.lang.Jednotky_units ?? 'jednotky',
      opts.data.lang.Jednotky_tens ?? 'desítky',
      opts.data.lang.Jednotky_hundreds ?? 'stovky',
      opts.data.lang.Jednotky_thousands ?? 'tisíce',
      opts.data.lang.Jednotky_ten_thousands ?? 'desetitisíce',
      opts.data.lang.Jednotky_hundred_thousands ?? 'stovky tisíc',
      opts.data.lang.Jednotky_millions ?? 'milionů',
    ];
    const bigFont = new PdfFont(PdfFont.FONT_NAME, 36);

    const pages: ReturnType<typeof pdf.addPage>[] = [];
    for (let i = 0; i < 6; i++) pages.push(pdf.addPage());

    for (let pageIdx = 0; pageIdx < pages.length; pageIdx++) {
      const page = pages[pageIdx];
      const count = pageIdx + 2;

      // Shuffle image indices
      const labels = Array.from({ length: Math.max(cX * cY, opts.list.length) }, (_, i) => i);
      for (let i = labels.length - 1; i > 0; i--) {
        const j = opts.data.random.next(0, i + 1);
        [labels[i], labels[j]] = [labels[j], labels[i]];
      }

      for (let x = 0; x < cX; x++) {
        for (let y = 0; y < cY; y++) {
          // Generate random number with 'count' digits
          const n0 = 1 + opts.data.random.next(0, 9);
          const nn = Array.from({ length: count - 1 }, () => opts.data.random.next(0, 10));
          const str = [n0, ...nn].join('');

          const rect = new PdfRect(x * size.width, y * size.height, size.width, size.height);
          page.drawRectangle(GUtil.cutPen, null, rect);

          const rowH = (rect.height - 2 * margin) / 3;
          const t1 = new PdfRect(rect.x + margin, rect.y + margin, rect.width - 2 * margin, rowH);
          const t2 = new PdfRect(t1.x, t1.y + t1.height, t1.width, rowH);
          const t3 = new PdfRect(t1.x, t2.y + t2.height, t1.width, rowH);

          // Draw image (faithfully preserving C# index calculation)
          const labelIdx = labels[(x * cY + cX) % opts.list.length];
          if (labelIdx < opts.list.length) {
            page.drawImageCenter(opts.list[labelIdx].image, t1);
          }

          // Draw number string
          page.drawString(str, bigFont, PdfBrush.Black, null, t2, ETextAlign.Center);

          // Draw unit place value boxes
          const sz = (t1.width - (count - 1) * margin) / count;
          const f = page.adjustFont(sz, names.slice(0, count), GUtil.baseFont);

          for (let i = 0; i < count; i++) {
            const r = new PdfRect(t3.x + i * (sz + margin), t3.y, sz, rowH);
            const r1 = new PdfRect(r.x, r.y + r.height - r.height / 3, r.width, r.height / 3);
            page.drawRoundedRectangle(GUtil.basePen, null, r, margin);
            page.drawString(names[count - i - 1], f, PdfBrush.Black, null, r1, ETextAlign.Center);
          }
        }
      }
    }
  }
}
